import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { twoFactorService } from '../../services/twoFactorService';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

export default function TwoFactorAuthenticationVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [verificationMethod, setVerificationMethod] = useState('authenticator');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [availableMethods, setAvailableMethods] = useState([]);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const inputRefs = useRef([]);

  // Get session data from location state (passed from SignIn)
  const sessionData = location?.state?.sessionData;
  const redirectPath = location?.state?.redirectPath || '/user-wallet-dashboard';

  useEffect(() => {
    loadAvailableMethods();
    loadDeviceInfo();
  }, []);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const loadAvailableMethods = async () => {
    try {
      const methods = await twoFactorService?.getMethods();
      const enabled = methods?.filter(m => m?.isEnabled);
      setAvailableMethods(enabled);
      
      if (enabled?.length > 0) {
        setVerificationMethod(enabled?.[0]?.methodType);
      }
    } catch (err) {
      console.error('Failed to load methods:', err);
    }
  };

  const loadDeviceInfo = () => {
    const info = {
      browser: navigator?.userAgent?.match(/(Chrome|Firefox|Safari|Edge)/)?.[0] || 'Unknown',
      os: navigator?.platform || 'Unknown',
      ipAddress: '•••.•••.•••.•••', // Would be fetched from backend
      location: 'Location unavailable', // Would be fetched from IP geolocation
      timestamp: new Date()?.toLocaleString()
    };
    setDeviceInfo(info);
  };

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/?.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs?.current?.[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e?.key === 'Backspace' && !code?.[index] && index > 0) {
      inputRefs?.current?.[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e?.preventDefault();
    const pastedData = e?.clipboardData?.getData('text')?.replace(/\D/g, '')?.slice(0, 6);
    const newCode = [...code];
    
    for (let i = 0; i < pastedData?.length; i++) {
      newCode[i] = pastedData?.[i];
    }
    
    setCode(newCode);
    
    // Focus last filled input or first empty
    const nextIndex = Math.min(pastedData?.length, 5);
    inputRefs?.current?.[nextIndex]?.focus();
  };

  const handleVerify = async () => {
    const verificationCode = code?.join('');
    
    if (verificationCode?.length !== 6) {
      setError('Please enter a complete 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // In production, this would verify the code with backend
      // For now, simulate verification
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Log security event
      await twoFactorService?.logSecurityEvent(
        'login_success',
        true,
        { method: verificationMethod, device_info: deviceInfo?.browser }
      );

      setSuccess(true);
      
      // Redirect after success
      setTimeout(() => {
        navigate(redirectPath);
      }, 1500);
    } catch (err) {
      setError(err?.message || 'Verification failed. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs?.current?.[0]?.focus();
      
      // Log failed attempt
      await twoFactorService?.logSecurityEvent(
        'login_failed',
        false,
        { method: verificationMethod, reason: 'invalid_code' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCountdown > 0) return;

    setLoading(true);
    setError('');

    try {
      // In production, trigger SMS resend
      await new Promise(resolve => setTimeout(resolve, 500));
      setResendCountdown(60);
      setSuccess(false);
      setCode(['', '', '', '', '', '']);
      inputRefs?.current?.[0]?.focus();
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUseBackupCode = () => {
    setVerificationMethod('backup');
    setCode(['', '', '', '', '', '']);
    setError('');
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'authenticator_app': return 'Smartphone';
      case 'sms': return 'MessageSquare';
      case 'hardware_key': return 'Key';
      case 'backup': return 'FileText';
      default: return 'Shield';
    }
  };

  const getMethodLabel = (method) => {
    switch (method) {
      case 'authenticator_app': return 'Authenticator App';
      case 'sms': return 'SMS Code';
      case 'hardware_key': return 'Hardware Key';
      case 'backup': return 'Backup Code';
      default: return 'Unknown';
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={32} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Successful!</h2>
          <p className="text-gray-600">Redirecting you to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Main Verification Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Shield" size={32} className="text-indigo-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Two-Factor Authentication</h1>
            <p className="text-gray-600">Enter the verification code to continue</p>
          </div>

          {/* Method Selector */}
          {availableMethods?.length > 1 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Verification Method</label>
              <div className="grid grid-cols-2 gap-3">
                {availableMethods?.map((method) => (
                  <button
                    key={method?.id}
                    onClick={() => {
                      setVerificationMethod(method?.methodType);
                      setCode(['', '', '', '', '', '']);
                      setError('');
                    }}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      verificationMethod === method?.methodType
                        ? 'border-indigo-600 bg-indigo-50' :'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon name={getMethodIcon(method?.methodType)} size={24} className="mx-auto mb-2" />
                    <p className="text-sm font-medium">{getMethodLabel(method?.methodType)}</p>
                    {method?.deviceName && (
                      <p className="text-xs text-gray-500 mt-1">{method?.deviceName}</p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Code Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {verificationMethod === 'backup' ? 'Enter Backup Code' : 'Enter 6-Digit Code'}
            </label>
            <div className="flex gap-2 justify-center" onPaste={handlePaste}>
              {code?.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e?.target?.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-indigo-600 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                  disabled={loading}
                />
              ))}
            </div>
            {verificationMethod === 'sms' && resendCountdown === 0 && (
              <button
                onClick={handleResendCode}
                disabled={loading}
                className="mt-3 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Resend Code
              </button>
            )}
            {resendCountdown > 0 && (
              <p className="mt-3 text-sm text-gray-500">
                Resend available in {resendCountdown}s
              </p>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleVerify}
              loading={loading}
              disabled={loading || code?.join('')?.length !== 6}
              fullWidth
              iconName="CheckCircle"
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </Button>

            {verificationMethod !== 'backup' && (
              <Button
                onClick={handleUseBackupCode}
                variant="outline"
                fullWidth
                iconName="FileText"
                disabled={loading}
              >
                Use Backup Code
              </Button>
            )}
          </div>
        </div>

        {/* Security Context Card */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Icon name="Info" size={20} className="text-indigo-600" />
            Login Attempt Details
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Browser:</span>
              <span className="font-medium text-gray-900">{deviceInfo?.browser}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Operating System:</span>
              <span className="font-medium text-gray-900">{deviceInfo?.os}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">IP Address:</span>
              <span className="font-medium text-gray-900">{deviceInfo?.ipAddress}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Time:</span>
              <span className="font-medium text-gray-900">{deviceInfo?.timestamp}</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 flex items-start gap-2">
              <Icon name="Shield" size={14} className="flex-shrink-0 mt-0.5" />
              <span>If you don't recognize this activity, cancel and secure your account immediately.</span>
            </p>
          </div>
        </div>

        {/* Help Link */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/signin')}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Cancel and return to sign in
          </button>
        </div>
      </div>
    </div>
  );
}